<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { useRoute, useRouter } from 'vue-router';
import { onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '~/stores/user-store';
import { handleErrorCode } from '~/utilities/handle-error-code';
import StarParticles from '~/components/StarParticles.vue';
import IconChevronDown from '~icons/carbon/chevron-down';
import IconError from '~icons/material-symbols/error';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const currentYear = new Date().getFullYear();
const API_AUTH = import.meta.env.PUBLIC_PYSCRIPT_API_AUTH;
const loginErrorMessage = handleErrorCode(route.query?.errorCode as string);

const pyscriptExamples = [
  {
    imgSrc: '/images/home/pyjokes.jpg',
    title: 'PyScript Jokes',
    appLink: 'https://examples.pyscriptapps.com/pyscript-jokes/latest/',
    codeLink: 'https://pyscript.com/@examples/pyscript-jokes/latest',
  },
  {
    imgSrc: '/images/home/hvplot-panel.png',
    title: 'hvPlot and Panel',
    appLink: 'https://examples.pyscriptapps.com/panel-and-hvplot/latest/',
    codeLink: 'https://pyscript.com/@examples/panel-and-hvplot/latest',
  },
  {
    imgSrc: '/images/home/tic-tac-toe.png',
    title: 'Tic-Tac-Toe',
    appLink: 'https://examples.pyscriptapps.com/tic-tac-toe/latest/',
    codeLink: 'https://pyscript.com/@examples/tic-tac-toe/latest',
  },
];

useHead({
  title: 'Code and Share Python in the Browser',
  meta: [
    {
      name: 'description',
      content:
        'Code and Share Python in the Browser. A vibrant community of makers, builders, and hackers building the next frontier of Python-powered web applications.',
    },
  ],
});

onBeforeMount(async () => {
  /**
   * If `psdc_redirect` is present in localStorage, redirect the user back to
   * the page they left off.
   */
  if (userStore.isLoggedIn) {
    const redirectUrlPath = localStorage.getItem('psdc_redirect');

    if (redirectUrlPath) {
      localStorage.removeItem('psdc_redirect');
      router.push(redirectUrlPath);
    } else {
      router.push({ name: 'dashboard' });
    }
  }
});
</script>

<template>
  <main v-if="!userStore.isLoggedIn">
    <section
      class="hero relative flex flex-col items-center justify-center px-5 pb-28 pt-8 text-center text-new-content-100"
    >
      <StarParticles class="absolute inset-0 z-0 h-full w-full" />

      <div class="container relative z-10 mb-16 flex w-full items-center gap-4 px-0 xl:mb-28">
        <svg aria-label="PyScript" class="block h-[40px] w-[200px]">
          <use xlink:href="/images/pyscript-logo.svg#pyscript-logo" />
        </svg>

        <div class="ml-auto" />

        <!-- TODO: Update once the WorkOS registration page is working properly. -->
        <!-- <a
          data-testid="registration"
          class="whitespace-nowrap rounded bg-new-green-500 px-4 py-2 text-sm font-semibold text-new-content-900 transition-colors hover:bg-new-green-700"
          :href="`${API_AUTH}/registration`"
          >{{ t('buttons.sign_up') }}</a
        > -->

        <a
          data-testid="login"
          class="whitespace-nowrap rounded border border-white px-4 py-2 text-sm font-semibold"
          :href="`${API_AUTH}/login`"
          >{{ t('buttons.sign_in') }}</a
        >
      </div>

      <h1 class="heading-xl relative z-10 mb-8 xl:mb-14">Code and Share Python in the Browser</h1>

      <p class="relative z-10 mx-auto mb-14 max-w-4xl text-xl xl:text-2xl">
        A vibrant community of makers, builders, and hackers building the next frontier of
        Python-powered web applications.
      </p>

      <div class="flex w-full justify-center gap-7">
        <!-- TODO: Update to point to the WorkOS sign up page once that's available. -->
        <a
          data-testid="login"
          class="green-btn relative z-10 w-full max-w-[280px] p-5"
          :href="`${API_AUTH}/login`"
          >{{ t('buttons.get_started') }}</a
        >
      </div>

      <section
        v-if="loginErrorMessage"
        class="relative z-10 mt-6 flex w-fit max-w-sm items-center gap-3 rounded border border-error-700 bg-error-200 px-3 py-3 text-error-700"
      >
        <IconError class="inline-block flex-none text-lg" />
        <p class="text-left text-sm">
          {{ loginErrorMessage }}
        </p>
      </section>
    </section>

    <section class="bg-new-gray-100 py-12 text-new-content-900 lg:py-20 xl:pb-0">
      <div class="container flex flex-col items-center justify-between gap-8 md:flex-row">
        <div class="text-center md:max-w-md md:text-left xl:max-w-none">
          <h2 class="heading-xl mb-6">Getting Started is Easy</h2>

          <div class="flex max-w-3xl flex-col gap-6 text-2xl font-medium">
            <p>
              Pyscript.com is a free platform that lets you create web applications with a suite of
              web-ready Python libraries.
            </p>

            <p>Join the PyScript universe, and make the impossible possible.</p>
          </div>

          <img
            src="/images/home/moon-bunny.png"
            loading="lazy"
            width="325"
            class="mt-16 hidden xl:block"
          />
        </div>

        <div class="mx-auto">
          <ol class="steps">
            <li>Create an account</li>
            <li>Start coding</li>
            <li>Run your project</li>
            <li>Share with the world</li>
          </ol>
        </div>
      </div>
    </section>

    <section class="bg-[#1c2021] py-16 text-new-content-100 xl:py-24">
      <div class="container text-center md:text-left">
        <h2 class="heading-xl mb-5">Create, Share, and Clone with Others</h2>
        <p class="mb-10 text-xl lg:text-2xl">
          See what others have created, build on their vision, or start a new project.
        </p>

        <div class="flex flex-col items-center gap-13 lg:flex-row lg:gap-20">
          <div
            v-for="ex in pyscriptExamples"
            v-once
            :key="ex.codeLink"
            class="flex w-full flex-col items-center justify-center gap-8"
          >
            <a
              :href="ex.appLink"
              :title="`Open ${ex.title} app in new tab`"
              class="transition-transform hover:scale-105"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                class="object-fit aspect-4/3 w-full max-w-lg rounded bg-new-gray-200"
                :src="ex.imgSrc"
                :alt="ex.title"
                loading="lazy"
              />
            </a>

            <a class="green-btn px-5 py-3" :href="ex.codeLink" :title="`View code for ${ex.title}`"
              >View Code</a
            >
          </div>
        </div>
      </div>
    </section>

    <section class="bg-new-gray-100 py-12 text-new-content-900 lg:py-20">
      <div class="container">
        <h2 class="heading-xl mb-7 text-center">FAQ</h2>

        <div class="mx-auto flex max-w-4xl flex-col gap-4">
          <details class="group rounded bg-white/60 px-6">
            <summary
              class="flex cursor-pointer list-none items-center justify-between py-4 text-xl font-bold"
            >
              What's the difference between pyscript.net and pyscript.com?
              <IconChevronDown
                class="flex-none transition-all duration-300 group-open:rotate-180"
              />
            </summary>

            <div class="flex flex-col gap-4 pb-4">
              <p>
                <a
                  href="https://pyscript.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="underline"
                  >Pyscript.net</a
                >
                is the home of the PyScript OSS project. It is free Open Source Software (OSS) that
                facilitates the creation, deployment, and sharing of Python applications, targeting
                both developers and non-developers by reducing friction in the process.
              </p>

              <p>
                Pyscript.com is a free service, built upon an open-source project that provides
                tools that simplify tasks for users of PyScript. This:
              </p>

              <ul class="list-disc pl-8">
                <li>
                  Eliminates the need for manual development tasks on local machines, such as using
                  an in-browser editor and hosting files online.
                </li>
                <li>Allows sharing applications with one click, simply sharing a link.</li>
                <li>
                  Scales infinitely (since it runs on the browser, it doesn't require complicated
                  and expensive infrastructure needs.
                </li>
                <li>Hosts a vibrant community of users that you can learn and copy from.</li>
              </ul>

              <p>This makes pyscript.com the best way to create, share and copy PyScript apps.</p>
            </div>
          </details>

          <details class="group rounded bg-white/60 px-6">
            <summary
              class="flex cursor-pointer list-none items-center justify-between py-4 text-xl font-bold"
            >
              Is pyscript.com free?
              <IconChevronDown
                class="flex-none transition-all duration-300 group-open:rotate-180"
              />
            </summary>

            <div class="pb-4">
              <p>
                Heck yeah! Pyscript.com is free and you can use it to create as many projects as you
                want.

                <!-- Heck yeah! Pyscript.com is a free tier that allows users to create, host and share unlimited pyscript projects. It also has paid tiers for more advanced features, such as extra space, private projects, security proxies and many more features. Please check out our plans (link to tiers page) for more information. -->
              </p>
            </div>
          </details>

          <details class="group rounded bg-white/60 px-6">
            <summary
              class="flex cursor-pointer list-none items-center justify-between py-4 text-xl font-bold"
            >
              How many apps can I create?
              <IconChevronDown
                class="flex-none transition-all duration-300 group-open:rotate-180"
              />
            </summary>

            <div class="pb-4">
              <p>
                You can create as many apps as you want. While we do have a limitation on the
                maximum size of an application (&lt;1mb), we don't have limitations on the number of
                apps you can create. Have fun! 😉
              </p>
            </div>
          </details>

          <details class="group rounded bg-white/60 px-6">
            <summary
              class="flex cursor-pointer list-none items-center justify-between py-4 text-xl font-bold"
            >
              Can I create an organization using pyscript.com?
              <IconChevronDown
                class="flex-none transition-all duration-300 group-open:rotate-180"
              />
            </summary>

            <div class="pb-4">
              <p>
                No, at the moment we don't support organizations, but it is a feature coming up in
                the near future.
              </p>
            </div>
          </details>

          <details class="group rounded bg-white/60 px-6">
            <summary
              class="flex cursor-pointer list-none items-center justify-between py-4 text-xl font-bold"
            >
              How can I contribute to the PyScript open-source framework?
              <IconChevronDown
                class="flex-none transition-all duration-300 group-open:rotate-180"
              />
            </summary>

            <div class="pb-4">
              <p>
                Read our
                <a
                  href="https://github.com/pyscript/pyscript/blob/main/CONTRIBUTING.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="underline"
                  >contribution guide</a
                >
                for more details.
              </p>
            </div>
          </details>
        </div>
      </div>
    </section>

    <footer class="bg-white text-new-content-900">
      <div
        class="container flex flex-col items-center justify-between gap-3 py-6 md:max-w-none md:flex-row md:px-10 xl:py-9"
      >
        <div>&copy;{{ currentYear }} Anaconda Inc. All rights reserved.</div>

        <div class="flex gap-2">
          <a
            class="underline"
            href="https://anaconda.com/legal"
            target="_blank"
            rel="noopener noreferrer"
            :title="t('links.legal')"
          >
            {{ t('links.legal') }}
          </a>
          <span>|</span>
          <a
            class="underline"
            href="https://legal.anaconda.com/policies"
            target="_blank"
            rel="noopener noreferrer"
            :title="t('links.privacy_policy')"
          >
            {{ t('links.privacy_policy') }}
          </a>
          <span>|</span>
          <a
            class="underline"
            href="https://community.anaconda.cloud/c/tech-topics/pyscript-dotcom"
            target="_blank"
            rel="noopener noreferrer"
            :title="t('links.help')"
          >
            {{ t('links.help') }}
          </a>
        </div>
      </div>
    </footer>
  </main>
</template>

<style scoped lang="postcss">
.steps {
  counter-reset: item;
  @apply mx-auto flex list-none flex-col gap-6;

  li {
    counter-increment: item;
    @apply flex items-center text-2xl font-bold lg:text-4xl;

    &::before {
      content: counter(item);
      @apply mr-6 flex h-13 w-13 flex-none items-center justify-center rounded-full bg-new-content-900 text-2xl font-bold text-new-content-100 xl:h-20 xl:w-20 xl:text-4xl;
    }
  }
}

.green-btn {
  @apply block rounded bg-new-green-500 text-center text-base font-bold text-new-content-900 transition-colors hover:bg-new-green-700;
}

.heading-xl {
  @apply text-3xl font-semibold leading-tight md:text-4xl xl:text-[56px] xl:leading-none;
  font-family: 'General Sans', sans-serif;
}

.hero {
  background-image: linear-gradient(180deg, #1c2021 0%, #172c2c 100%);
}
</style>
